import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  Grid,
  IconButton,
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import nbLocale from "date-fns/locale/nb";

import Alert, { ALERT_TYPES } from "../Components/Alert";
import DragDrop from "../Components/trips/DragDrop";
import ImgBar from "../Components/trips/ImgBar";
import ContentContainer from "../Components/ContentContainer";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const EditTrip = ({ customerId, refreshList, alertTripName, refreshLinks }) => {
  const infoListRef = useRef();
  const [tripName, setTripName] = useState("");
  let { tripId } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [previewURL, setPreviewURL] = useState("");
  const [theme, setTheme] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [tripInfo, setTripInfo] = useState({
    tripName: "",
    mapCoordinates: "",
  });
  const [infoList, setInfoList] = useState([]);

  const [fileList, setFileList] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const [isImgBar, setIsImgBar] = useState(false);
  const [image, setImage] = useState(null);
  const [nameError, setNameError] = useState("");

  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const [firstUpdate, setFirstUpdate] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  var dayjs = require("dayjs");

  useEffect(() => {
    const getTripInfo = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_GET_ONE_TRIP}/${tripId}/${customerId}`,
        {
          method: "GET",
          type: "cors",
          credentials: "include",
        }
      );
      if (response.status === 200) {
        const data = await response.json();
        setTripInfo((prevState) => ({
          ...prevState,
          tripName: data.name,
          mapCoordinates: `${data.mapCoordinates ?? ""}`,
        }));

        setDateRange([new Date(data.startDateUtc), new Date(data.endDateUtc)]);

        setTheme(data.theme);
      } else {
        setAlertMessage("Oops, kunne ikke hente reisen.");
        setOpenAlert(true);
      }
    };

    try {
      getTripInfo();
    } catch (error) {
      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  }, [customerId, tripId]);

  //GET TRIP DATA
  useEffect(() => {
    const getTripData = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_GET_ONE_TRIP}/${tripId}/${customerId}`,
        {
          method: "GET",
          type: "cors",
          credentials: "include",
        }
      );
      if (response.status === 200) {
        const data = await response.json();
        setTripName(data.name);
        data.travelTips && setInfoList([...data.travelTips]);

        data.travelDocuments && setFileList([...data.travelDocuments]);

        setPreviewURL(data.image?.url);
      } else {
        setAlertMessage("Oops, kunne ikke hente reisen.");
        setOpenAlert(true);
      }
    };

    try {
      getTripData();
    } catch (error) {
      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  }, [customerId, tripId, isLoading]);

  const closeTrip = useCallback(() => {
    const pathname = location.pathname;
    const lastSlash = pathname.lastIndexOf("/");
    navigate(pathname.substr(0, lastSlash));
  }, [location.pathname, navigate]);

  // name & mapCoordinates
  const tripInfoHandler = (e) => {
    const { value, name } = e.currentTarget;
    setTripInfo((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const titleRef = useRef();
  const descriptionRef = useRef();
  const linkRef = useRef();
  //extra info list
  const addInfo = async () => {
    const infoItem = {
      id: new Date().getTime(),
      title: titleRef.current.children[0].children[0].value,
      description: descriptionRef.current.children[0].children[0].value,
      link: linkRef.current.children[0].children[0].value,
      order: infoList.length + 1,
    };

    setInfoList((prev) => [...prev, infoItem]);
    setExpanded(infoItem.id.toString());
    setIsDisabled(true);

    const { title, description, link, order } = infoItem;

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_ADD_TRAVEL_TIP}/${tripId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          credentials: "include",
          type: "cors",
          body: JSON.stringify([{ title, description, link, order }]),
        }
      );
      setIsLoading(false);

      if (response.status === 200) {
        titleRef.current.children[0].children[0].value = "";
        descriptionRef.current.children[0].children[0].value = "";
        linkRef.current.children[0].children[0].value = "";
      } else {
        setAlertMessage("Oops, kunne ikke opprette reisen.");
        setOpenAlert(true);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  };

  const deleteInfo = async (id) => {
    const newInfos = [...infoList].filter((info) => info.id !== id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DELETE_TRAVEL_TIP}/${id}`,
        {
          method: "DELETE",
          credentials: "include",
          type: "cors",
        }
      );
      if (response.status === 200) {
        setInfoList(newInfos);
      } else {
        setAlertMessage("Oops, kunne ikke slette reisetips.");
        setOpenAlert(true);
      }
    } catch (error) {
      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  };

  const handleExpanded = (id) => (e, isExpanded) => {
    setExpanded(isExpanded ? id : false);
    setFirstUpdate(true);
    if (!firstUpdate) {
      setInfoList(infoList.map((item, index) => ({ ...item, order: index })));
    }
  };

  //For drag&drop
  const removeFile = async (file) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DELETE_TRAVEL_DOCUMENT}/${file.id}`,
        {
          method: "DELETE",
          credentials: "include",
          type: "cors",
        }
      );
      if (response.status === 200) {
        const updatedList = [...fileList];
        updatedList.splice(fileList.indexOf(file), 1);
        setFileList(updatedList);
      } else {
        setAlertMessage("Oops, kunne ikke slette reisedokument.");
        setOpenAlert(true);
      }
    } catch (error) {
      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  };

  const addFile = async (e) => {
    const newFiles = e.target.files;
    const formDocument = new FormData();
    var nyeFiler = fileList;
    for (var i = 0; i < newFiles.length; i++) {
      newFiles[i].order = fileList.length + 1;
      nyeFiler.push(newFiles[i]);
      formDocument.append("files", newFiles[i]);
    }
    formDocument.append("tripId", tripId);
    setFileList((prevState) => {
      return [...prevState, ...newFiles];
    });

    setFileList(nyeFiler);

    try {
      setIsLoading(true);

      const res = await fetch(process.env.REACT_APP_ADD_TRAVEL_DOCUMENT, {
        method: "POST",
        credentials: "include",
        type: "cors",
        body: formDocument,
      });
      setIsLoading(false);

      if (res.status !== 200) {
        setAlertMessage("Oops, kunne ikke legge til reisedokument.");
        setOpenAlert(true);
      }
    } catch (error) {
      setIsLoading(false);

      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  };

  //toggle
  const toggleHandler = (e, theme) => {
    if (theme !== null) {
      setTheme(theme);
    }
  };

  //img uploader
  const openImgBar = () => setIsImgBar(true);
  const closeImgBar = () => setIsImgBar(false);

  const imageHandler = (img, imgUrl) => {
    setImage(img);
    setPreviewURL(imgUrl);
  };

  const postImage = async () => {
    if (image instanceof File) {
      let formImg = new FormData();
      formImg.append("image", image);
      try {
        const response = await fetch(process.env.REACT_APP_ADD_IMAGE, {
          method: "POST",
          credentials: "include",
          type: "cors",
          body: formImg,
        });
        if (response.status === 200) {
          return await response.json();
        } else {
          setAlertMessage("Oops, kunne ikke laste opp bildet.");
          setOpenAlert(true);
          return null;
        }
      } catch (error) {
        console.error("Error:", error);
        setAlertMessage("Oops, noe gikk galt! 😰");
        setOpenAlert(true);
        return null;
      }
    }

    return null;
  };

  const editTrip = async (imageId) => {
    if (imageId) {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_CHANGE_IMAGE}/${imageId}/${tripId}`,
          {
            method: "POST",
            credentials: "include",
            type: "cors",
          }
        );
        if (res.status !== 200) {
          setAlertMessage("Oops, kunne ikke endre bilde.");
          setOpenAlert(true);
        }
      } catch (error) {
        console.error("Error:", error);
        setAlertMessage("Oops, noe gikk galt! 😰");
        setOpenAlert(true);
      }
    }

    const formData = new FormData();
    formData.append("id", tripId);
    formData.append("name", tripInfo.tripName);

    formData.append(
      "startDateUtc",
      dayjs(dateRange[0]).format().replace(/-/g, "/").replace(/T.+/, "")
    );
    formData.append(
      "endDateUtc",
      dayjs(dateRange[1]).format().replace(/-/g, "/").replace(/T.+/, "")
    );
    formData.append("theme", theme);
    formData.append("travelTips", infoList);
    formData.append("files", fileList);
    formData.append("MapCoordinates", tripInfo.mapCoordinates);
    if (!dateRange[0] || !dateRange[1]) {
      setAlertMessage("Dato kan ikke være tom!");
      setOpenAlert(true);
    }

    if (tripInfo.tripName === "") {
      setNameError("Reisenavn kan ikke være tom");
    }

    if (dateRange[0] && dateRange[1] && tripInfo.tripName) {
      setIsLoading(true);

      try {
        const response = await fetch(process.env.REACT_APP_EDIT_TRIP, {
          method: "POST",
          type: "cors",
          credentials: "include",
          body: formData,
        });
        setIsLoading(false);
        if (response.status === 200) {
          alertTripName(`${tripInfo.tripName} har blitt oppdatert! 🙌`);
          refreshList();
          closeTrip();
        } else {
          setAlertMessage("Oops, kunne ikke redigere reisen.");
          setOpenAlert(true);
        }
      } catch (error) {
        setIsLoading(false);
        console.error("Error:", error);
        setAlertMessage("Oops, noe gikk galt! 😰");
        setOpenAlert(true);
      }
    }
    updateTravelListOrder();
    editTravelDocument();
  };

  const editTravelDocument = async () => {
    const newestFiles = fileList.map((item, index) => ({
      ...item,
      order: index,
    }));
    try {
      await fetch(process.env.REACT_APP_UPDATE_TRAVEL_DOCUMENT, {
        method: "POST",
        type: "cors",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(newestFiles),
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  };
  const updateTravelListOrder = async () => {
    try {
      await fetch(process.env.REACT_APP_CHANGE_ORDER + `/${tripId}`, {
        method: "POST",
        type: "cors",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(infoList),
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error:", error);
      setAlertMessage("Oops, noe gikk galt! 😰");
      setOpenAlert(true);
    }
  };

  const editTripHandler = async () => {
    if (image === null) {
      editTrip(null);
      return;
    }
    const dbImg = await postImage();
    if (!(image instanceof File) && dbImg === null) {
      editTrip(image.id);
      return;
    }

    editTrip(dbImg.id);
  };

  const handleFilesOrder = (list) => {
    setFileList(list);
  };

  const moveUp = (index) => {
    if (index > 0) {
      changeOrder(infoList, index, index - 1);
    }
  };

  const moveDown = (index) => {
    if (index < infoList.length - 1) {
      changeOrder(infoList, index, index + 1);
    }
  };

  const changeOrder = (obj, prop1, prop2) => {
    var tmp = obj[prop1];
    obj[prop1] = obj[prop2];
    obj[prop2] = tmp;
    const newInfo = obj;
    setInfoList(newInfo.map((item, index) => ({ ...item, order: index })));
    refreshLinks();
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const orderedItems = reorder(
      infoList,
      result.source.index,
      result.destination.index
    );
    setInfoList(orderedItems.map((item, index) => ({ ...item, order: index })));
    //setInfoList(orderedItems);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  return (
    <ContentContainer title={tripName}>
      <Box sx={{ width: { xs: "100%", lg: "60%" }, px: 2 }}>
        <Grid
          container
          rowSpacing={{ xs: 3, md: 4 }}
          columnSpacing={4}
          py={{ xs: 2, sm: 0 }}
        >
          {/* previewImg & button to open ImgSidebar, check if admin is superadmin*/}
          <Grid item xs={12}>
            <Typography variant="h4">bilde</Typography>
            {!previewURL ? (
              <Button
                onClick={openImgBar}
                variant="outlined"
                sx={{
                  width: { xs: "100%", sm: "60%", md: "50%" },
                  height: "20vh",
                  fontSize: "16px",
                  textTransform: "none",
                }}
              >
                Trykk her for å velge bilde
              </Button>
            ) : (
              <Box position="relative">
                <Button
                  onClick={openImgBar}
                  variant="outlined"
                  sx={{
                    width: { xs: "100%", sm: "60%", md: "50%" },

                    height: "20vh",
                    border: "1px dashed",
                    padding: 1,
                  }}
                >
                  <img
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                    src={previewURL}
                    alt="img"
                  />
                </Button>
              </Box>
            )}

            <ImgBar
              isImg={isImgBar}
              closeImgBar={closeImgBar}
              imageHandler={imageHandler}
            />
          </Grid>
          {/* Toggle button*/}
          {/* TODO: make color more prominent */}
          <Grid item xs={12}>
            <Typography variant="h4">Temafarge</Typography>
            <ToggleButtonGroup
              size="small"
              color="info"
              value={theme}
              exclusive
              onChange={toggleHandler}
            >
              <ToggleButton
                value={false}
                color="secondary"
                sx={{
                  fontWeight: "bold",

                  "&.MuiToggleButton-root.Mui-selected": {
                    backgroundColor: "rgba(144, 114, 93, 0.3)",
                  },
                }}
              >
                Brun
              </ToggleButton>
              <ToggleButton
                value={true}
                color="primary"
                sx={{
                  fontWeight: "bold",

                  "&.MuiToggleButton-root.Mui-selected": {
                    backgroundColor: "rgba(33, 131, 126, 0.3)",
                  },
                }}
              >
                Grønn
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          {/* date-picker */}
                  <Grid item xs={12}>
                      <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={nbLocale}
                      >
                          <Stack direction="row" spacing={4} width="100%">
                              <Stack sx={{ width: "100%" }}>
                                  <Typography variant="h4">Startdato</Typography>
                                  <DatePicker
                                      value={dateRange[0]}
                                      onChange={(newValue) => {
                                          setDateRange([newValue, dateRange[1]]);
                                      }}
                                      renderInput={(params) => <TextField {...params} />}
                                  />
                              </Stack>

                              <Stack sx={{ width: "100%" }}>
                                  <Typography variant="h4">Sluttdato</Typography>
                                  <DatePicker
                                      value={dateRange[1]}
                                      onChange={(newValue) => {
                                          setDateRange([dateRange[0], newValue]);
                                      }}
                                      renderInput={(params) => <TextField {...params} />}
                                  />
                              </Stack>
                          </Stack>
                      </LocalizationProvider>
                  </Grid>


          {/* name-input */}
          <Grid item xs={6}>
            <Typography variant="h4">navn</Typography>

            <TextField
              error={!!nameError}
              helperText={nameError}
              onClick={() => setNameError("")}
              id="name"
              name="tripName"
              placeholder="Reisenavn"
              variant="outlined"
              onChange={tripInfoHandler}
              value={tripInfo.tripName}
              required
              sx={{ width: "100%" }}
            />
          </Grid>
          {/* map link-input */}
          <Grid item xs={6}>
            <Typography variant="h4">koordinater</Typography>
            <TextField
              placeholder="Koordinater fra kart"
              variant="outlined"
              required
              name="mapCoordinates"
              onChange={tripInfoHandler}
              value={tripInfo.mapCoordinates}
              sx={{ width: "100%" }}
            />
          </Grid>
          {/* Create info list item */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4">Reisetips</Typography>
            <Stack spacing={2}>
              <TextField
                ref={titleRef}
                placeholder="Tittel"
                onChange={(e) => {
                  e.currentTarget.value
                    ? setIsDisabled(false)
                    : setIsDisabled(true);
                }}
                variant="outlined"
              />
              <TextField
                ref={descriptionRef}
                placeholder="Beskrivelse"
                variant="outlined"
                multiline
                rows={6}
              />
              <TextField ref={linkRef} placeholder="Lenke" variant="outlined" />
            </Stack>
            <LoadingButton
              loading={isLoading}
              onClick={addInfo}
              disabled={isDisabled}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Legg til
            </LoadingButton>
          </Grid>
          {/* Extra info list */}
          <Grid
            ref={infoListRef}
            item
            xs={12}
            md={6}
            sx={{ mt: { xs: 0, md: 5.8 } }}
          >
            {infoList.length > 0 && (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {infoList
                        .sort((a, b) => (a.order > b.order ? 1 : -1))
                        .map((info, index) => {
                          return (
                            <Draggable
                              key={info.id}
                              draggableId={info.id.toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Accordion
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  key={index}
                                  expanded={expanded === info.id.toString()}
                                  onChange={handleExpanded(info.id.toString())}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ wordBreak: "break-word" }}
                                  >
                                    <Typography
                                      variant="h4"
                                      fontSize={16}
                                      p={0}
                                      pr={1}
                                    >
                                      {info.title}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails
                                    sx={{ position: "relative", pb: 7 }}
                                  >
                                    <Stack spacing={1}>
                                      <Typography
                                        variant="h4"
                                        fontSize={16}
                                        p={0}
                                      >
                                        Beskrivelse
                                      </Typography>
                                      <Typography>
                                        {info.description}
                                      </Typography>
                                      <Typography
                                        variant="h4"
                                        fontSize={16}
                                        p={0}
                                        paddingTop={2}
                                      >
                                        Link
                                      </Typography>
                                      <Typography
                                        sx={{ wordBreak: "break-all" }}
                                      >
                                        {info.link}
                                      </Typography>
                                      <IconButton
                                        onClick={() => deleteInfo(info.id)}
                                        sx={{
                                          position: "absolute",
                                          bottom: 8,
                                          left: 8,
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 24 }} />
                                      </IconButton>
                                      <AccordionDetails></AccordionDetails>
                                      <IconButton
                                        onClick={() => moveUp(index)}
                                        sx={{
                                          position: "absolute",
                                          bottom: 40,
                                          right: 8,
                                        }}
                                      >
                                        <ArrowUpwardIcon
                                          sx={{ fontSize: 24 }}
                                        />
                                      </IconButton>
                                      <IconButton
                                        onClick={() => moveDown(index)}
                                        sx={{
                                          position: "absolute",
                                          bottom: 8,
                                          right: 8,
                                        }}
                                      >
                                        <ArrowDownwardIcon
                                          sx={{ fontSize: 24 }}
                                        />
                                      </IconButton>
                                    </Stack>
                                  </AccordionDetails>
                                </Accordion>
                              )}
                            </Draggable>
                          );
                        })}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Grid>
          {/* Drag&Drop */}
          <DragDrop
            fileList={fileList}
            fileDrop={addFile}
            dropClose={removeFile}
            handleFilesOrder={handleFilesOrder}
            refreshLinks={refreshLinks}
          />
          {/* Create trip button */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "start" },
            }}
          >
            <LoadingButton
              loading={isLoading}
              variant="contained"
              sx={{
                mt: { xs: 4, md: 12 },
                width: { xs: "80%", md: "0" },
                px: 6,
              }}
              onClick={editTripHandler}
            >
              Lagre
            </LoadingButton>
          </Grid>
        </Grid>

        {/* ERROR ALERTS */}
        <Alert
          state={openAlert}
          setState={setOpenAlert}
          type={ALERT_TYPES.ERROR}
          duration={2000}
          message={alertMessage}
        />
      </Box>
    </ContentContainer>
  );
};
export default EditTrip;
